import { axiosDefaultInstance } from '@/http/default'
import type { VehicleBulkCreateRs, VehicleRequest } from '@/model/Vehicle'

import type Page from '@/model/Page'
import type Vehicle from '@/model/Vehicle'
import type { VehicleImportField } from '@/utils/vehicleImportUtils'
import { useProfileStore } from '@/store/profile.module'
import type { AxiosInstance } from 'axios'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

class VehicleService {
  private axiosInstance: AxiosInstance
  private storeProfile: any

  constructor() {
    this.axiosInstance = axiosDefaultInstance
    // this.storeProfile = useProfileStore()
  }

  async findAll(): Promise<Vehicle[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Vehicle[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllCsv(name?: string, plate?: string): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []
    if (name) {
      params.push(['name', name])
    }
    if (plate) {
      params.push(['plate', plate])
    }
    if (locale) {
      params.push(['locale', locale])
    }

    const searchParams = new URLSearchParams(params)
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/csv`,
        { params: searchParams, responseType: 'blob' }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findById(id: string): Promise<Vehicle | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Vehicle>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/${id}`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importCsv(
    file: File,
    columnMapping: { columnIndex: number; field: VehicleImportField }[],
    delimiter: string
  ): Promise<VehicleBulkCreateRs | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('columnMapping', JSON.stringify({
        columns: columnMapping,
        delimiter,
      }))

      const networkResponse = await this.axiosInstance.post<VehicleBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/imports/csv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createVehicle(vehicle: VehicleRequest): Promise<Vehicle | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.post<Vehicle>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle`,
        vehicle
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateVehicle(vehicle: VehicleRequest): Promise<Vehicle | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.put<Vehicle>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/${vehicle.id}`,
        vehicle
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async deleteVehicle(id: string): Promise<Vehicle | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.delete<Vehicle>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findVehicleDeliveryNotes(
    page: number,
    size: number,
    startDate: string,
    endDate: string
  ): Promise<Page<Vehicle> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []
    params.push(['page', String(page)])
    params.push(['size', String(size)])
    params.push(['startDate', startDate])
    params.push(['endDate', endDate])

    const searchParams = new URLSearchParams(params)
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<Vehicle>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/vehicle/delivery-notes`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const vehicleService = new VehicleService()
